import { bff, createBFFHandlers } from '~/framework/bff';
import { z } from 'zod';
import { getServerSideMonolithClient } from '~/framework';
import { Guide, getGuide } from '~/monolith-api/guides';
import { SignoffConfigForStep, getSignoffConfigForStep } from '~/monolith-api/signoffs';
import { StepForm, getStepForm } from '~/monolith-api/step-forms/getStepForm';
import { CommonRequestOptions } from '~/monolith-api/makeRequest';

export type GetGuideStepOptions = CommonRequestOptions & {
   guideId: number;
   stepId: number;
   useSourceLang: boolean;
};

export type GetGuideStepResult = {
   guide: Guide;
   stepForm: StepForm | undefined;
   signoffConfig: SignoffConfigForStep;
};

const optionsSchema = z.object({
   guideId: z.number(),
   stepId: z.number(),
   useSourceLang: z.boolean(),
});

export default createBFFHandlers({
   post: async (req, res) => {
      const monolithClient = getServerSideMonolithClient(req);
      const options = optionsSchema.parse(req.body);
      const commonOptions = { useSourceLang: options.useSourceLang, apiClient: monolithClient };

      const guide = await getGuide(options.guideId, commonOptions);

      if (!guide) {
         res.status(404).json({ error: 'Guide not found' });
         return;
      }

      const [stepForm, signoffConfig] = await Promise.all([
         getStepForm(options.stepId, guide.guide_revisionid, commonOptions).catch(error => {
            if (error.response.data.message === 'Endpoint is disabled') {
               return undefined;
            }
         }),
         getSignoffConfigForStep(options.stepId, commonOptions).catch(error => {
            return {
               type: 'none',
            } as GetGuideStepResult['signoffConfig'];
         }),
      ]);

      const guideStep: GetGuideStepResult = {
         guide: guide,
         stepForm: stepForm,
         signoffConfig: signoffConfig,
      };

      res.status(200).json(guideStep);
   },
});

export const getGuideStep = async (options: GetGuideStepOptions): Promise<GetGuideStepResult> => {
   return bff()
      .post('/guides/getGuideStep', options)
      .then(response => response.data);
};
